import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";

export type ResourcedTexts = Record<string, string>;

export type newsletterFormProps = {
  resourcedTexts: ResourcedTexts;
  showTagline: boolean;
  newsletterCodename: string;
  endpointUrl: string;
  successUrl: string;
};

type newsletterFormValues = {
  firstname: string;
  surname: string;
  email: string;
  privacyPolicy: boolean;
};

export default function NewsletterForm({
  resourcedTexts,
  showTagline,
  newsletterCodename,
  endpointUrl,
  successUrl,
}: newsletterFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<newsletterFormValues>({ mode: "onTouched" });

  const [submitError, setSubmitError] = useState<string | null>(null);

  function onSubmit(formData: newsletterFormValues) {
    fetch(endpointUrl, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...formData,
        newsletterCodename,
      }),
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          window.location.href = successUrl;
        } else {
          setSubmitError("Something went wrong, please try again later");
        }
      })
      .catch((err) => setSubmitError(err));
  }

  return (
    <section
      className="form-section"
      id="newsletter-subscription"
      data-anchor="#newsletter-subscription"
    >
      <div className="container">
        <div className="row">
          <div className="col-lg-5">
            <div className="form-section__content">
              <h2 className={clsx("h1 form-section__title", showTagline && "tagline")}>
                {resourcedTexts.newsletterTitle}
              </h2>
              <p className="form-section__text">{resourcedTexts.newsletterDescription}</p>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="form-section__form">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Without the Kentico classes the form loses alot of its styling */}
                <div className="ktc-two-columns-section">
                  <div className="field-wrapper double-item">
                    <div className="form-field">
                      <div className="editing-form-control-nested-control">
                        <label className="control-label" htmlFor="first-name">
                          {resourcedTexts.firstnameLabel}
                        </label>
                        <input
                          className="form-control"
                          id="first-name"
                          placeholder={resourcedTexts.firstnamePlaceholder}
                          type="text"
                          {...register("firstname", { required: true })}
                        />
                        {errors.firstname && (
                          <div className="field-validation-error">
                            {resourcedTexts.firstnameEmpty}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-field">
                      <div className="editing-form-control-nested-control">
                        <label className="control-label" htmlFor="surname">
                          {resourcedTexts.surnameLabel}
                        </label>
                        <input
                          className="form-control"
                          id="surname"
                          placeholder={resourcedTexts.surnamePlaceholder}
                          type="text"
                          {...register("surname", { required: true })}
                        />
                        {errors.surname && (
                          <div className="field-validation-error">
                            {resourcedTexts.surnameEmpty}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ktc-default-section">
                    <div className="form-field">
                      <div className="editing-form-control-nested-control">
                        <label className="control-label" htmlFor="email">
                          {resourcedTexts.emailLabel}
                        </label>
                        <input
                          className="form-control"
                          id="email"
                          placeholder={resourcedTexts.emailPlaceholder}
                          type="email"
                          {...register("email", {
                            required: true,
                            pattern: {
                              value: /\S+@\S+\.\S+/,
                              message: resourcedTexts.emailInvalid,
                            },
                          })}
                        />
                        {errors.email && errors.email.type === "required" && (
                          <div className="field-validation-error">{resourcedTexts.emailEmpty}</div>
                        )}
                        {errors.email && errors.email.type === "pattern" && (
                          <div className="field-validation-error">{errors.email.message}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-field">
                      <div className="editing-form-control-nested-control">
                        <span className="ktc-checkbox" title="">
                          <input
                            className="form-control"
                            id="privacy-policy"
                            type="checkbox"
                            value="true"
                            {...register("privacyPolicy", { required: true })}
                          />
                          <label
                            htmlFor="privacy-policy"
                            dangerouslySetInnerHTML={{ __html: resourcedTexts.consent }}
                          ></label>
                        </span>
                        {errors.privacyPolicy && (
                          <div className="field-validation-error">
                            {resourcedTexts.consentEmpty}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-field form-section__submit-row">
                      <button type="submit" className="button button--primary form-section__submit">
                        {resourcedTexts.buttonText}
                      </button>
                      {submitError && <div className="field-validation-error">{submitError}</div>}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
